<script lang="ts">
	import type { PageProps } from "./$types";
	import { Section } from "#lib/web-ui/primitives/index.js";
	import { InvestmentManagementForm } from "#lib/web-ui/forms/index.js";
	import { FaqOne } from "#lib/web-ui/blocks/faq/index.js";
	import { CtaBanner } from "#lib/web-ui/blocks/index.js";
	import PhoneIcon from "@lucide/svelte/icons/phone";
	import MailIcon from "@lucide/svelte/icons/mail";
	import MapPinIcon from "@lucide/svelte/icons/map-pin";
	import ExternalLinkIcon from "@lucide/svelte/icons/external-link";
	import ChartBarIcon from "@lucide/svelte/icons/chart-bar";
	import { PUBLIC_GOOGLE_PLACES_TOKEN } from "$app/env/public";
	import { cloudinaryUrl } from "#lib/shared/cloudinary.js";

	let { data }: PageProps = $props();

	const profile = {
		name: "Brian H. Ferguson",
		title: "Founder & Managing Partner",
		imageSrc: cloudinaryUrl("fergmarcapital-com/brian-ferguson-photo", {
			width: 120,
			height: 120,
			crop: "fill"
		}),
		imageAlt: "Brian H. Ferguson"
	};

	const calendlyUrl = "https://calendly.com/brianh-ferguson/invest-some-time-with-brian";
	const addressQuery = encodeURIComponent(
		`${data.contactInfo.address.street}, ${data.contactInfo.address.suite}, ${data.contactInfo.address.city}, ${data.contactInfo.address.state} ${data.contactInfo.address.zip}`
	);
</script>

<svelte:head>
	<title>Contact Fergmar Capital | Schedule a Call - Victoria, TX</title>
	<meta
		name="description"
		content="Contact Fergmar Capital to discuss passive real estate investment opportunities. Schedule a call with Brian Ferguson or visit us in Victoria, TX. {data
			.contactInfo.phone}."
	/>

	<meta property="og:title" content="Contact Fergmar Capital | Schedule a Call" />

	<meta
		property="og:description"
		content="Discuss passive real estate investment offerings. Schedule a call with our team or visit our Victoria, TX office."
	/>

	<meta name="twitter:title" content="Contact Fergmar Capital | Schedule a Call" />

	<meta
		name="twitter:description"
		content="Discuss passive real estate investment offerings. Schedule a call with our team or visit our Victoria, TX office."
	/>

	{@html `<script type="application/ld+json">${JSON.stringify({
		"@context": "https://schema.org",
		"@type": "ContactPage",
		name: "Contact Fergmar Capital",
		description:
			"Get in touch with Fergmar Capital to discuss passive real estate investment offerings.",
		url: "https://fergmarcapital.com/contact",
		mainEntity: {
			"@type": "InvestmentCompany",
			name: "Fergmar Capital",
			telephone: data.contactInfo.phone,
			email: data.contactInfo.email,
			address: {
				"@type": "PostalAddress",
				streetAddress: `${data.contactInfo.address.street}, ${data.contactInfo.address.suite}`,
				addressLocality: data.contactInfo.address.city,
				addressRegion: data.contactInfo.address.state,
				postalCode: data.contactInfo.address.zip,
				addressCountry: "US"
			},
			openingHoursSpecification: {
				"@type": "OpeningHoursSpecification",
				dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
				opens: "08:00",
				closes: "17:00"
			},
			geo: {
				"@type": "GeoCoordinates",
				latitude: 28.8252,
				longitude: -96.9853
			}
		}
	})}</script>`}

	{@html `<script type="application/ld+json">${JSON.stringify({
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: data.faqItems.map((item) => ({
			"@type": "Question",
			name: item.question,
			acceptedAnswer: { "@type": "Answer", text: item.answer }
		}))
	})}</script>`}
</svelte:head>

<p class="sr-only">
	Contact Fergmar Capital to discuss passive real estate investment offerings in Victoria, TX.
	Schedule a call with Founder Brian H. Ferguson, visit our office at 4800 N Navarro Suite 100, or
	email invest@fergmarcapital.com. We respond within 24-48 hours.
</p>

<!-- Hero -->
<Section theme="muted" class="pb-10 md:pb-12">
	<div class="text-center">
		<p class="text-primary text-[1.17rem] font-bold">Contact Us</p>
		<h1
			class="text-foreground mx-auto mt-3 max-w-2xl text-4xl font-semibold tracking-tight sm:text-5xl"
		>
			Contact Fergmar Capital
		</h1>

		<p class="text-muted-foreground mx-auto mt-6 max-w-2xl text-lg">
			Whether you're exploring investment offerings, have questions about our portfolio, or
			want to learn how passive real estate investing can work for you, our team is ready to
			help.
		</p>
	</div>
</Section>

<!-- Form + Sidebar + Map -->
<Section theme="light" class="pt-10 md:pt-12">
	<div class="grid grid-cols-1 items-start gap-12 lg:grid-cols-3">
		<!-- Form (2/3) -->
		<div class="lg:col-span-2">
			<h2 class="text-foreground text-2xl font-semibold">Send Us a Message</h2>
			<p class="text-muted-foreground mt-2 mb-8">
				Fill out the form below and a member of our team will be in touch within 24–48
				hours.
			</p>
			<InvestmentManagementForm />
		</div>

		<!-- Sidebar (1/3) - sticky so it follows the form -->
		<div class="space-y-6 lg:sticky lg:top-24 lg:col-span-1">
			<!-- Contact info -->
			<div class="bg-muted rounded-lg border p-6">
				<h3 class="text-foreground text-lg font-semibold">Contact Information</h3>
				<ul class="mt-5 space-y-5">
					<li class="flex items-start gap-3">
						<div
							class="bg-primary/15 flex size-9 shrink-0 items-center justify-center rounded-full"
						>
							<MapPinIcon class="text-primary size-4" />
						</div>
						<a
							href={data.contactInfo.addressLink}
							target="_blank"
							class="text-foreground text-sm leading-snug hover:underline"
						>
							{data.contactInfo.address.street}, {data.contactInfo.address.suite}<br
							/>
							{data.contactInfo.address.city}, {data.contactInfo.address.state}
							{data.contactInfo.address.zip}
						</a>
					</li>
					<li class="flex items-start gap-3">
						<div
							class="bg-primary/15 flex size-9 shrink-0 items-center justify-center rounded-full"
						>
							<PhoneIcon class="text-primary size-4" />
						</div>
						<div>
							<a
								href="tel:{data.contactInfo.phone.replace(/\D/g, '')}"
								class="text-foreground text-sm font-medium hover:underline"
							>
								{data.contactInfo.phone}
							</a>
							<p class="text-muted-foreground text-xs">Mon–Fri, 8am–5pm CST</p>
						</div>
					</li>
					<li class="flex items-start gap-3">
						<div
							class="bg-primary/15 flex size-9 shrink-0 items-center justify-center rounded-full"
						>
							<MailIcon class="text-primary size-4" />
						</div>
						<div>
							<a
								href="mailto:{data.contactInfo.email}"
								class="text-foreground text-sm font-medium hover:underline"
							>
								{data.contactInfo.email}
							</a>
							<p class="text-muted-foreground text-xs">
								We'll respond within 24–48 hours
							</p>
						</div>
					</li>
				</ul>
			</div>

			<!-- Brian card -->
			<div class="bg-muted rounded-lg border p-6">
				<div class="flex gap-4">
					<img
						src={profile.imageSrc}
						alt={profile.imageAlt}
						class="h-14 w-14 shrink-0 rounded-full object-cover"
						width="56"
						height="56"
					/>
					<div class="flex flex-col justify-center">
						<h3 class="text-foreground font-semibold">{profile.name}</h3>
						<p class="text-muted-foreground text-sm font-medium">{profile.title}</p>
					</div>
				</div>
				<p class="text-muted-foreground mt-4 text-sm leading-relaxed">
					Prefer a conversation? Schedule directly on Brian's calendar. It's the fastest
					way to connect.
				</p>
				<a
					href={calendlyUrl}
					target="_blank"
					class="bg-primary text-primary-foreground hover:bg-primary/80 mt-4 flex w-full items-center justify-center gap-2 rounded px-4 py-2.5 text-sm font-semibold transition-colors"
				>
					Schedule a Call
					<ExternalLinkIcon class="size-4" />
				</a>
			</div>
		</div>
	</div>

	<!-- Map - inside the same section, no extra padding gap -->
	<div class="mt-12 overflow-hidden rounded-lg border">
		<div class="aspect-[21/9] w-full">
			<iframe
				title="Office Location"
				width="100%"
				height="100%"
				frameborder="0"
				loading="lazy"
				style="border:0"
				src={`https://www.google.com/maps/embed/v1/place?key=${PUBLIC_GOOGLE_PLACES_TOKEN}&q=${addressQuery}`}
				allowfullscreen
			></iframe>
		</div>
	</div>
</Section>

<!-- CTA -->
<CtaBanner
	title="Existing Investor?"
	subtitle="Access your secure dashboard to view portfolio performance"
	buttonHref="https://fergmarcapital.invportal.com/login"
	buttonText="Investor Portal Login"
	icon={ChartBarIcon}
/>

<!-- FAQ -->
<FaqOne
	subtitle="Get answers to common questions about real estate investment with Fergmar Capital."
	items={data.faqItems}
	contactHref="/contact"
/>
